import { HierarchyObject, HierarchyObjectMap, type Fact, type Hierarchical } from '../base'
import { Publisher } from '../pubsub/publisher'
import { type PushMessage, type Subscription, type subscriptionFunction } from '../pubsub/subscription'
import { RE_FIXED_PREFIX } from './types'

import { type Storage, type StorageSettings } from './types'

export type InMemoryStorageSettings = StorageSettings // & {}

export function isStoredFact (object: unknown): object is StoredFact {
  return object instanceof StoredFact || Object.prototype.hasOwnProperty.call(object, 'fact')
}

export class StoredFact extends HierarchyObject implements Hierarchical {
  fact: Fact<any>
  constructor (id: string, fact: Fact<any>) {
    super(id)
    this.fact = fact
  }
}

export class InMemoryStore implements Storage {
  private readonly items: HierarchyObjectMap
  private _name: string = 'inMemory'
  private _isOpen: boolean = false
  private readonly publisher: Publisher<Fact<any>>

  constructor () {
    this.items = new HierarchyObjectMap()
    this.publisher = new Publisher<Fact<any>>()
  }

  async pushsync (sub: Subscription<Fact<any>>, pattern: string, notify: subscriptionFunction<Fact<any>>): Promise<void> {
    let scope = ''
    const prefix = RE_FIXED_PREFIX.exec(pattern)
    if (prefix) {
      scope = prefix[0]
    }
    let error: any
    try {
      const keys = await this.keys(scope)
      for (const path of keys) {
        if (sub.matches(path)) {
          const fact = await this.get(path)
          if (fact) {
            const msg: PushMessage<Fact<any>> = { topic: path, data: fact }
            notify(msg)
          }
        }
      }
    } catch (reason) {
      error = reason
    }
    await new Promise<void>((resolve, reject) => {
      if (error) {
        reject(error)
      } else {
        resolve()
      }
    })
  }

  async subscribe (pattern: string, notify: subscriptionFunction<Fact<any>>, pushsync: boolean): Promise<void> {
    const sub = this.publisher.subscribe(pattern, notify)
    if (pushsync) {
      await this.pushsync(sub, pattern, notify); return
    }
    await new Promise<void>((resolve, reject) => {
      resolve()
    })
  }

  unsubscribe (pattern: string, notify?: subscriptionFunction<Fact<any>> | undefined): boolean {
    return this.publisher.unsubscribe(pattern, notify)
  }

  async open (config?: InMemoryStorageSettings): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      this._name = config?.name ?? 'inMemory'
      this._isOpen = true
      resolve()
    })
  }

  close (): void {
    this.items.clear()
  }

  async get (path: string): Promise<Fact<any> | null> {
    return await new Promise<Fact<any> | null>((resolve, reject) => {
      if (!this._isOpen) {
        reject(Error('Store is not open'))
      }
      const stored = this.items.get(path)
      if (!stored) {
        resolve(null)
      }
      if (isStoredFact(stored)) {
        resolve(stored?.fact ?? null)
      } else {
        resolve(null)
      }
    })
  }

  async set (path: string, fact: Fact<any>): Promise<Fact<any>> {
    return await new Promise<Fact<any>>((resolve, reject) => {
      if (!this._isOpen) {
        reject(Error('Store is not open'))
      }
      const parts = path.split('.')
      this.items.add(path, new StoredFact(parts[parts.length - 1], fact))
      this.publisher.publish(path, fact)
      resolve(fact)
    })
  }

  async keys (scope: string): Promise<IterableIterator<string>> {
    return await new Promise<IterableIterator<string>>((resolve, reject) => {
      if (!this._isOpen) {
        reject(Error('Store is not open'))
      }
      const root = this.items.get(scope)
      if (!root) {
        resolve([][Symbol.iterator]())
        return
      }
      resolve(root.children.getFlatMap().keys())
    })
  }

  async delete (path: string): Promise<boolean> {
    return await new Promise<boolean>((resolve, reject) => {
      if (!this._isOpen) {
        reject(Error('Store is not open'))
      }
      // this.publisher.publish(path, undefined)
      resolve(this.items.delete(path))
    })
  }

  async clear (): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      if (!this._isOpen) {
        reject(Error('Store is not open'))
      }
      this.items.clear()
    })
  }

  get name (): string {
    return this._name
  }
}
